import { useRecordContext } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { ShowButton } from "@/components/admin/show-button";
import { Separator } from "@/components/ui/separator";

import type { Prospect } from "../types";
import { ProspectGroupsEdit } from "./ProspectGroupsEdit";
import { ProspectPrevNextButtons } from "./ProspectPrevNextButtons";
import { ProspectRefreshButton } from "./ProspectRefreshButton";
import { ProspectSendMailButton } from "./ProspectSendMailButton";
import { ProspectSkipTraceButton } from "./ProspectSkipTraceButton";
import { ProspectSyncAcButton } from "./ProspectSyncAcButton";

const usd = (value?: number | null) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(value);

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-right font-medium break-words">{value}</span>
    </div>
  );
};

// Whole-years age from an ISO birthday, or null.
const ageFromBirthday = (iso?: string | null) => {
  if (!iso) return null;
  const b = new Date(iso);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age >= 0 && age < 120 ? age : null;
};

const oneLineAddress = (
  street?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null,
) => {
  const line = [street, [city, state].filter(Boolean).join(", "), zip]
    .filter(Boolean)
    .join(" · ");
  return line || null;
};

export const ProspectAside = ({
  link = "edit",
}: {
  link?: "show" | "edit";
}) => {
  const record = useRecordContext<Prospect>();
  if (!record) return null;

  return (
    <div className="w-full lg:w-72 shrink-0 flex flex-col gap-4">
      <div className="flex justify-end">
        {/* link="edit" means we're on the Show page; keep prev/next on the same page type */}
        <ProspectPrevNextButtons linkType={link === "edit" ? "show" : "edit"} />
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <ProspectSendMailButton />
        <ProspectSyncAcButton />
        <ProspectSkipTraceButton />
        <ProspectRefreshButton />
        {link === "edit" ? <EditButton /> : <ShowButton />}
      </div>

      <Section title="Lists">
        <ProspectGroupsEdit prospectId={Number(record.id)} />
      </Section>

      <Section title="Owner">
        <Fact label="Owner" value={record.owner_name} />
        <Fact label="Co-owner" value={record.co_owner_name} />
        {(record.phones ?? [])
          .filter((p) => p?.number)
          .map((p, i) => (
            <Fact key={`ph${i}`} label={`${p.type} phone`} value={p.number} />
          ))}
        {(record.emails ?? [])
          .filter((e) => e?.email)
          .map((e, i) => (
            <Fact key={`em${i}`} label={`${e.type} email`} value={e.email} />
          ))}
        <Fact label="Occupancy" value={record.occupancy} />
        <Fact label="Owner age" value={ageFromBirthday(record.owner_birthday)} />
        <Fact label="Owned (yrs)" value={record.length_of_ownership_yrs} />
        <Fact
          label="Mailing"
          value={oneLineAddress(
            record.mailing_address,
            record.mailing_city,
            record.mailing_state,
            record.mailing_zip,
          )}
        />
      </Section>

      <Section title="Property">
        <Fact label="Type" value={record.property_type} />
        <Fact label="Style" value={record.style} />
        <Fact
          label="Beds / Baths"
          value={
            record.beds != null || record.baths != null
              ? `${record.beds ?? "—"} / ${record.baths ?? "—"}`
              : null
          }
        />
        <Fact label="Sqft" value={record.sqft?.toLocaleString()} />
        <Fact label="Lot (acres)" value={record.lot_acres} />
        <Fact label="Stories" value={record.stories} />
        <Fact label="Year built" value={record.year_built} />
        <Fact label="Subdivision" value={record.subdivision} />
        <Fact label="County" value={record.county} />
        <Fact label="APN" value={record.parcel_apn} />
        <Fact label="Legal" value={record.legal_description} />
      </Section>

      <Section title="Valuation">
        <Fact label="Tax value" value={usd(record.tax_value)} />
        <Fact
          label="Tax amount"
          value={
            record.tax_amount != null
              ? `${usd(record.tax_amount)}${record.tax_year ? ` (${record.tax_year})` : ""}`
              : null
          }
        />
        <Fact label="AVM" value={usd(record.avm)} />
        <Fact label="Est. equity" value={usd(record.estimated_equity)} />
        <Fact label="Orig. mortgage" value={usd(record.original_mortgage)} />
        <Fact label="List price" value={usd(record.list_price)} />
        <Fact label="Last sold" value={record.sold_date} />
        <Fact label="Last sold price" value={usd(record.sold_amount)} />
      </Section>

      <Section title="Pre-Auction">
        <Fact label="Auction date" value={record.auction_date} />
        <Fact label="Type" value={record.auction_type} />
        <Fact label="Status" value={record.auction_status} />
        <Fact label="Result" value={record.auction_result} />
        <Fact label="Opening bid" value={usd(record.opening_bid)} />
        <Fact label="Min bid" value={usd(record.min_bid)} />
        <Fact
          label="Lien amount"
          value={
            record.lien_amount != null
              ? `${usd(record.lien_amount)}${record.lien_position ? ` (pos ${record.lien_position})` : ""}`
              : null
          }
        />
        <Fact label="Judgment" value={usd(record.judgment_amount)} />
        <Fact label="Case #" value={record.case_number} />
        <Fact label="Plaintiff" value={record.plaintiff_lender} />
        <Fact label="Trustee" value={record.trustee} />
        <Fact label="Sale location" value={record.sale_location} />
        <Fact label="Redemption" value={record.redemption_deadline} />
      </Section>

      <Section title="Listing">
        <Fact label="Status" value={record.listing_status} />
        <Fact label="List date" value={record.list_date} />
        <Fact label="DOM" value={record.days_on_market} />
        <Fact label="MLS ID" value={record.mls_id} />
        <Fact label="Agent" value={record.list_agent} />
        <Fact label="Office" value={record.list_office} />
        <Fact label="Status change" value={record.status_change_date} />
      </Section>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <h6 className="text-sm font-semibold">{title}</h6>
    <Separator />
    {children}
  </div>
);
